import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/* ─── Governo Italiano palette ─── */
const G = {
  bg: '#FFFFFF',
  surface: '#F5F7FA',
  surfaceAlt: '#EBF0F5',
  primary: '#0066CC',
  primaryDark: '#003366',
  primaryLight: '#4A90D9',
  teal: '#00C5CA',
  text: '#17324D',
  textSecondary: '#5A768A',
  textMuted: '#8DA4B8',
  border: '#D9DADB',
  borderLight: '#E8EBED',
  white: '#FFFFFF',
  success: '#008758',
  warning: '#A66300',
};

const fontLink = "https://fonts.googleapis.com/css2?family=Titillium+Web:wght@300;400;600;700;900&display=swap";

/* ─── Nav ─── */
const GovNav = () => {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const h = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', h);
    return () => window.removeEventListener('scroll', h);
  }, []);

  return (
    <nav data-testid="gov-nav" style={{ fontFamily: "'Titillium Web', sans-serif" }}
      className={`fixed top-0 left-0 right-0 z-[9999] transition-all duration-300`}>
      {/* Top blue bar */}
      <div style={{ background: G.primaryDark, height: '4px' }} />
      <div style={{ background: scrolled ? 'rgba(255,255,255,0.97)' : G.white, borderBottom: `1px solid ${G.border}`, transition: 'all 0.3s' }}
        className={scrolled ? 'shadow-sm' : ''}>
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
          <Link to="/versione-b" className="flex items-center gap-3" data-testid="gov-logo">
            <div className="w-10 h-10 rounded flex items-center justify-center" style={{ background: G.primary }}>
              <span className="text-white font-black text-sm">AI</span>
            </div>
            <div>
              <span className="font-bold text-lg tracking-tight" style={{ color: G.text }}>
                &lt;/AI_covo.tech&gt;<sup style={{ fontSize: '8px' }}>®</sup>
              </span>
              <span className="block text-[10px] uppercase tracking-[0.2em] font-semibold" style={{ color: G.primary }}>
                Digital Hub Sociale
              </span>
            </div>
          </Link>
          <div className="hidden md:flex items-center gap-8">
            {['Doposcuola', 'Formazione', 'Chi Siamo', 'Dona'].map(label => (
              <Link key={label} to={label === 'Chi Siamo' ? '/about' : `/${label.toLowerCase()}`}
                className="text-sm font-semibold transition-colors"
                style={{ color: G.textSecondary }}
                onMouseEnter={e => e.target.style.color = G.primary}
                onMouseLeave={e => e.target.style.color = G.textSecondary}>
                {label}
              </Link>
            ))}
          </div>
          <Link to="/test-ab" className="text-xs px-4 py-2 rounded border font-semibold transition-all hover:scale-105"
            style={{ borderColor: G.primary, color: G.primary }} data-testid="gov-back-ab">
            A/B Test
          </Link>
        </div>
      </div>
    </nav>
  );
};

/* ─── Hero ─── */
const GovHero = () => (
  <section data-testid="gov-hero" className="relative min-h-[85vh] flex items-center overflow-hidden" style={{ background: G.surface }}>
    {/* Subtle geometric pattern */}
    <div className="absolute inset-0 opacity-[0.03]" style={{
      backgroundImage: `repeating-linear-gradient(45deg, ${G.primary} 0, ${G.primary} 1px, transparent 0, transparent 50%)`,
      backgroundSize: '20px 20px'
    }} />
    {/* Left blue accent bar */}
    <div className="absolute left-0 top-0 w-1.5 h-full" style={{ background: G.primary }} />

    <div className="relative z-10 max-w-6xl mx-auto px-6 pt-32 pb-20 w-full">
      <div className="grid md:grid-cols-2 gap-12 items-center">
        <div>
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded mb-6" style={{ background: G.primary + '10', border: `1px solid ${G.primary}20` }}>
            <div className="w-2 h-2 rounded-full" style={{ background: G.primary }} />
            <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: G.primary }}>Valdelsa, Toscana</span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.08] mb-8" style={{ color: G.text }}>
            Una scuola di visionari,{' '}
            <span style={{ color: G.primary }}>aperta a tutti.</span>
          </h1>
          <p className="text-base sm:text-lg leading-relaxed mb-10 max-w-lg" style={{ color: G.textSecondary }}>
            Doposcuola, formazione digitale, inclusione sociale e cultura del fare.
            Il sapere non ha età, il futuro si costruisce insieme.
          </p>
          <div className="flex flex-wrap gap-4">
            <Link to="/doposcuola" data-testid="gov-cta-scopri"
              className="inline-flex items-center gap-2 px-8 py-4 font-bold text-sm uppercase tracking-wider rounded transition-all hover:shadow-lg"
              style={{ background: G.primary, color: G.white }}>
              Scopri il Progetto
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" /></svg>
            </Link>
            <Link to="/dona" data-testid="gov-cta-dona"
              className="inline-flex items-center px-8 py-4 font-bold text-sm uppercase tracking-wider rounded border-2 transition-all hover:shadow-md"
              style={{ borderColor: G.primary, color: G.primary, background: G.white }}>
              Sostienici
            </Link>
          </div>
        </div>
        {/* Right side: key numbers */}
        <div className="hidden md:grid grid-cols-2 gap-4">
          {[
            { num: '6-18', label: 'Anni Doposcuola', icon: 'M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253' },
            { num: '100%', label: 'Deducibile', icon: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z' },
            { num: '5+', label: 'Comunità attive', icon: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z' },
            { num: '0€', label: "Per chi ha bisogno", icon: 'M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z' },
          ].map((item, i) => (
            <div key={i} className="p-6 rounded-lg border text-center" style={{ background: G.white, borderColor: G.borderLight }}>
              <svg className="w-8 h-8 mx-auto mb-3" style={{ color: G.primary }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d={item.icon} />
              </svg>
              <p className="text-2xl font-black" style={{ color: G.primary }}>{item.num}</p>
              <p className="text-xs font-semibold mt-1" style={{ color: G.textMuted }}>{item.label}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  </section>
);

/* ─── Services ─── */
const GovServices = () => {
  const services = [
    { icon: 'M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253', title: 'Doposcuola', desc: 'Supporto educativo quotidiano per bambini e ragazzi.' },
    { icon: 'M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z', title: 'Formazione Digitale', desc: 'Corsi detraibili 100% per professionisti.' },
    { icon: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z', title: 'Anziani Digitali', desc: 'Alfabetizzazione tecnologica per la terza età.' },
    { icon: 'M21 15.546c-.523 0-1.046.151-1.5.454a2.704 2.704 0 01-3 0 2.704 2.704 0 00-3 0 2.704 2.704 0 01-3 0 2.704 2.704 0 00-3 0 2.704 2.704 0 01-3 0A1.75 1.75 0 013 15.546', title: 'Spazi Sociali', desc: 'Weekend per famiglie a basso reddito.' },
    { icon: 'M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z', title: 'Laboratori', desc: 'Recupero per bambini con difficoltà.' },
    { icon: 'M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064', title: 'Educazione Civica', desc: 'Sostenibilità e cittadinanza attiva.' },
  ];

  return (
    <section data-testid="gov-services" className="py-20" style={{ background: G.white }}>
      <div className="max-w-6xl mx-auto px-6">
        <div className="text-center mb-14">
          <div className="w-12 h-1 mx-auto mb-4" style={{ background: G.primary }} />
          <h2 className="text-3xl sm:text-4xl font-bold mb-2" style={{ color: G.text }}>I Nostri Servizi</h2>
          <p className="text-sm" style={{ color: G.textSecondary }}>Un hub per tutti — nessuno escluso.</p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((s, i) => (
            <div key={i} className="p-6 rounded-lg border transition-all hover:shadow-md hover:-translate-y-0.5"
              style={{ background: G.surface, borderColor: G.borderLight }}>
              <div className="w-12 h-12 rounded-lg flex items-center justify-center mb-4" style={{ background: G.primary + '10' }}>
                <svg className="w-6 h-6" style={{ color: G.primary }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d={s.icon} />
                </svg>
              </div>
              <h3 className="text-lg font-bold mb-2" style={{ color: G.text }}>{s.title}</h3>
              <p className="text-sm leading-relaxed" style={{ color: G.textSecondary }}>{s.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ─── Quote ─── */
const GovQuote = () => (
  <section data-testid="gov-quote" className="py-20" style={{ background: G.surface }}>
    <div className="max-w-4xl mx-auto px-6 text-center">
      <div className="p-10 rounded-lg border-l-4" style={{ background: G.white, borderColor: G.primary }}>
        <svg className="w-10 h-10 mx-auto mb-6" style={{ color: G.primary + '30' }} fill="currentColor" viewBox="0 0 24 24">
          <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10H14.017zM0 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10H0z" />
        </svg>
        <blockquote className="text-xl sm:text-2xl font-bold italic leading-snug mb-6" style={{ color: G.text }}>
          "Quando entri nell'AI_COVO, qualcuno tirerà fuori il meglio da te."
        </blockquote>
        <p className="text-sm font-semibold uppercase tracking-wider" style={{ color: G.primary }}>Maieutica — Socrate</p>
      </div>
    </div>
  </section>
);

/* ─── Community Counter ─── */
const GovCommunity = () => {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    axios.get(`${API_URL}/api/community/stats`).then(r => setStats(r.data)).catch(() => {});
  }, []);

  const sections = [
    { key: 'genz', label: 'Agora GenZ', count: stats?.genz || 0 },
    { key: 'boomer', label: 'Boomer & GenX', count: stats?.boomer || 0 },
    { key: 'senatori', label: 'I Senatori', count: stats?.senatori || 0 },
    { key: 'inclusivo', label: 'Spazio Inclusivo', count: stats?.inclusivo || 0 },
    { key: 'sport', label: 'Sport', count: stats?.sport || 0 },
  ];
  const total = sections.reduce((s, x) => s + x.count, 0);

  return (
    <section data-testid="gov-community" className="py-20" style={{ background: G.white }}>
      <div className="max-w-5xl mx-auto px-6 text-center">
        <div className="w-12 h-1 mx-auto mb-4" style={{ background: G.primary }} />
        <p className="text-xs font-semibold uppercase tracking-[0.25em] mb-3" style={{ color: G.primary }}>La Comunità Cresce</p>
        <p className="text-5xl sm:text-6xl font-black mb-2" style={{ color: G.primary }}>{total}</p>
        <p className="text-sm mb-10" style={{ color: G.textSecondary }}>anime al Covo — in tempo reale</p>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
          {sections.map(s => (
            <div key={s.key} className="p-4 rounded-lg border text-center" style={{ background: G.surface, borderColor: G.borderLight }}>
              <p className="text-2xl font-black" style={{ color: G.primary }}>{s.count}</p>
              <p className="text-[10px] uppercase tracking-wider font-semibold mt-1" style={{ color: G.textMuted }}>{s.label}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ─── CTA Sostienici ─── */
const GovCTA = () => (
  <section data-testid="gov-cta" className="py-24" style={{ background: G.surface }}>
    <div className="max-w-4xl mx-auto px-6 text-center">
      <div className="p-10 rounded-lg border" style={{ background: G.white, borderColor: G.border }}>
        <p className="text-xs font-semibold uppercase tracking-[0.25em] mb-4" style={{ color: G.success }}>Sostienici</p>
        <h2 className="text-3xl sm:text-4xl font-bold mb-6" style={{ color: G.text }}>
          Che cintura sei?
        </h2>
        <p className="text-base mb-10 max-w-lg mx-auto" style={{ color: G.textSecondary }}>
          Come nel karate, ogni livello ha il suo valore. Da 1€ a 999€ aziendale —
          tutto <strong style={{ color: G.text }}>deducibile al 100%</strong>.
        </p>
        <div className="flex flex-wrap justify-center gap-3 mb-10">
          {[
            { belt: 'Bianca', price: '1€', color: '#9CA3AF' },
            { belt: 'Arancione', price: '5€', color: '#f97316' },
            { belt: 'Verde', price: '10€', color: '#22c55e' },
            { belt: 'Blu', price: '15€', color: '#3b82f6' },
            { belt: 'Rossa', price: '50€', color: '#ef4444' },
          ].map(b => (
            <div key={b.belt} className="px-5 py-3 rounded-lg border text-center min-w-[80px]"
              style={{ borderColor: b.color + '30', background: G.surface }}>
              <p className="text-lg font-black" style={{ color: b.color }}>{b.price}</p>
              <p className="text-[10px] uppercase tracking-wider" style={{ color: G.textMuted }}>{b.belt}</p>
            </div>
          ))}
        </div>
        <Link to="/dona" data-testid="gov-dona-cta"
          className="inline-flex items-center gap-2 px-10 py-4 font-bold text-sm uppercase tracking-wider rounded transition-all hover:shadow-lg"
          style={{ background: G.primary, color: G.white }}>
          Dona Ora
        </Link>
      </div>
    </div>
  </section>
);

/* ─── Info Band ─── */
const GovInfoBand = () => (
  <section data-testid="gov-info-band" className="py-12" style={{ background: G.primaryDark }}>
    <div className="max-w-6xl mx-auto px-6">
      <div className="grid sm:grid-cols-3 gap-8 text-center">
        {[
          { label: 'Associazione Culturale', desc: 'Iscritta al Registro' },
          { label: 'Erogazioni Liberali', desc: 'Deducibili al 100%' },
          { label: 'Aperto a Tutti', desc: 'Nessun requisito di accesso' },
        ].map((item, i) => (
          <div key={i}>
            <p className="text-lg font-bold text-white">{item.label}</p>
            <p className="text-sm" style={{ color: 'rgba(255,255,255,0.6)' }}>{item.desc}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

/* ─── Footer ─── */
const GovFooter = () => (
  <footer data-testid="gov-footer" className="py-16 border-t" style={{ background: G.white, borderColor: G.border }}>
    <div className="max-w-6xl mx-auto px-6">
      <div className="grid md:grid-cols-4 gap-10 mb-12">
        <div>
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 rounded flex items-center justify-center" style={{ background: G.primary }}>
              <span className="text-white font-black text-xs">AI</span>
            </div>
            <span className="font-bold" style={{ color: G.text }}>AI_covo.tech</span>
          </div>
          <p className="text-sm" style={{ color: G.textMuted }}>Colle di Val d'Elsa (SI)<br />Toscana, Italia</p>
        </div>
        {[
          { title: 'Servizi', links: [['Doposcuola', '/doposcuola'], ['Formazione', '/formazione'], ['Inclusione', '/hub']] },
          { title: 'Risorse', links: [['Brand Kit', '/brand'], ['Sostienici', '/dona'], ['RoadMap', '/links']] },
          { title: 'Contatti', links: [['info@aicovo.it', '#'], ['WhatsApp', 'https://wa.me/393273839782']] },
        ].map(col => (
          <div key={col.title}>
            <h4 className="text-xs font-semibold uppercase tracking-wider mb-4" style={{ color: G.primary }}>{col.title}</h4>
            <ul className="space-y-2">
              {col.links.map(([label, href]) => (
                <li key={label}>
                  <Link to={href} className="text-sm transition-colors"
                    style={{ color: G.textMuted }}
                    onMouseEnter={e => e.target.style.color = G.primary}
                    onMouseLeave={e => e.target.style.color = G.textMuted}>
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
      <div className="pt-8 border-t flex flex-col md:flex-row justify-between items-center gap-4" style={{ borderColor: G.border }}>
        <p className="text-xs" style={{ color: G.textMuted }}>© 2030 AI_COVO · Digital Hub Sociale</p>
        <p className="text-xs" style={{ color: G.textMuted }}>&lt;/BUILT_FOR_COMMUNITY/&gt;</p>
      </div>
    </div>
  </footer>
);

/* ─── Main Page ─── */
const VersioneGovPage = () => (
  <div data-testid="versione-gov-page" style={{ background: G.bg, color: G.text, fontFamily: "'Titillium Web', sans-serif" }}>
    <link href={fontLink} rel="stylesheet" />
    <GovNav />
    <GovHero />
    <GovServices />
    <GovQuote />
    <GovCommunity />
    <GovCTA />
    <GovInfoBand />
    <GovFooter />
  </div>
);

export default VersioneGovPage;
